import api from '@/api'
import Vue from 'vue'
import {vue} from '@/main.js'

// Server LocalDateTime is serialized in UTC as "YYYY-MM-DD HH:mm" (minute precision),
// so the timestamp alone can't order messages sent in the same minute. messageId is a
// Mongo ObjectId (hex, time-prefixed + counter) on both CRM and webtrader, so it is a
// reliable chronological tie-breaker. Pending (optimistic) messages always sort last.
const compareMessages = (a, b) => {
  if (!!a.pending !== !!b.pending) return a.pending ? 1 : -1
  const t1 = String(a.timestamp || '')
  const t2 = String(b.timestamp || '')
  if (t1 !== t2) return t1 < t2 ? -1 : 1
  const id1 = String(a.messageId || '')
  const id2 = String(b.messageId || '')
  if (id1 === id2) return 0
  return id1 < id2 ? -1 : 1
}

const emptyChat = (sessionId) => ({ userId: sessionId, unseenMsgs: 0, messages: [] })

const ensureChat = (state, sessionId) => {
  if (!state.chatMap[sessionId]) {
    Vue.set(state.chatMap, sessionId, emptyChat(sessionId))
  }
  return state.chatMap[sessionId]
}

// Upserts messages into the session's chat; returns true when something new was added.
const mergeMessages = (state, sessionId, messages) => {
  const chat = ensureChat(state, sessionId)
  const list = chat.messages.slice()
  let added = false
  messages.forEach(message => {
    if (message == null || message.messageId == null) return
    const existingIndex = list.findIndex(m => m.messageId === message.messageId)
    if (existingIndex !== -1) {
      list.splice(existingIndex, 1, { ...list[existingIndex], ...message })
      return
    }
    // A real message of mine that arrived (poll / websocket) before the send response:
    // replace the matching optimistic copy instead of showing it twice.
    const pendingIndex = list.findIndex(m => m.pending && !m.failed
      && m.senderId === message.senderId && m.content === message.content)
    if (pendingIndex !== -1) {
      list.splice(pendingIndex, 1, { ...message, localKey: list[pendingIndex].localKey })
      return
    }
    list.push(message)
    added = true
  })
  list.sort(compareMessages)
  chat.messages = list
  return added
}

export default {
  namespaced: true,
  state: {
    sessions: {},
    activeChat: null,
    chatMap: {}, // Map to store chat data by session ID
    drafts: {}, // Unsent input text by session ID — survives re-renders / session switches
    typing: {}, // sessionId -> timestamp (ms) of the last "client typing" event
  },
  getters: {
    sessions(state) {
      return Object.values(state.sessions)
    },
    activeSession(state) {
      return state.activeChat
    },
    chatMap(state) {
      return state.chatMap
    },
    chatBySessionId: (state) => (sessionId) => {
      return state.chatMap[sessionId] || null
    },
    unseenMsgsBySessionId: (state) => (sessionId) => {
      return state.sessions[sessionId]?.unseenMsgs || 0
    },
    hasActiveChat(state) {
      return state.activeChat !== null
    },
    hasChats(state) {
      return Object.keys(state.chatMap).length > 0
    },
    draftBySessionId: (state) => (sessionId) => {
      return state.drafts[sessionId] || ''
    },
    typingBySessionId: (state) => (sessionId) => {
      return state.typing[sessionId] || null
    },
  },
  mutations: {
    setSessions(state, sessions) {
      const next = {}
      ;(sessions || []).forEach(session => {
        const previous = state.sessions[session.id]
        next[session.id] = { ...session, unseenMsgs: session.unseenMsgs != null ? session.unseenMsgs : (previous?.unseenMsgs || 0) }
      })
      state.sessions = next
      // Keep the open chat pointing at fresh data, but never drop it here: dropping it
      // unmounts the whole chat window (and whatever the agent was typing).
      if (state.activeChat && next[state.activeChat.id]) {
        state.activeChat = next[state.activeChat.id]
      }
    },
    setSession(state, session) {
      if (session == null || session.id == null) return
      const previous = state.sessions[session.id]
      const merged = { ...(previous || {}), ...session, unseenMsgs: session.unseenMsgs != null ? session.unseenMsgs : (previous?.unseenMsgs || 0) }
      Vue.set(state.sessions, session.id, merged)
      if (state.activeChat && state.activeChat.id === session.id) {
        state.activeChat = merged
      }
    },
    removeSession(state, sessionId) {
      Vue.delete(state.sessions, sessionId)
      Vue.delete(state.chatMap, sessionId)
      Vue.delete(state.drafts, sessionId)
      if(state.activeChat && state.activeChat.id === sessionId){
        state.activeChat = null
      }
    },
    setActiveChat(state, sessionId) {
      if(sessionId == null){
        state.activeChat = null
      }else {
        state.activeChat = state.sessions[sessionId] || null
      }
    },
    setSessionUnseen(state, { sessionId, unseenMsgs }) {
      const session = state.sessions[sessionId]
      if (session) {
        Vue.set(state.sessions, sessionId, { ...session, unseenMsgs })
      }
    },
    setSessionLastMessage(state, message) {
      const sessionId = message?.chatSessionId || message?.sessionId
      const session = state.sessions[sessionId]
      if (!session || message.pending) return
      if (session.lastMessage && compareMessages(session.lastMessage, message) > 0) return
      const updated = { ...session, lastMessage: message, lastMessageTime: message.timestamp }
      Vue.set(state.sessions, sessionId, updated)
      if (state.activeChat && state.activeChat.id === sessionId) {
        state.activeChat = updated
      }
    },
    updateChat(state, messages) {
      if (messages == null || messages.length === 0) {
        return
      }
      const sessionId = messages[0].chatSessionId
      if (sessionId == null) return
      const added = mergeMessages(state, sessionId, messages)
      if (added) {
        vue.$root.$emit('system::chat::messages::updated', sessionId)
      }
    },
    addMessage(state, message) {
      const added = mergeMessages(state, message.sessionId, [message])
      if (added) {
        vue.$root.$emit('system::chat::messages::updated', message.sessionId)
      }
    },
    updateMessage(state, {tempId, message, sessionId }) {
      const chat = ensureChat(state, sessionId)
      const list = chat.messages.slice()
      const tempIndex = list.findIndex(msg => msg.messageId === tempId)
      const realIndex = message?.messageId != null ? list.findIndex(msg => msg.messageId === message.messageId) : -1
      if (tempIndex !== -1 && realIndex !== -1) {
        // Real message already arrived via poll/websocket — just drop the optimistic copy
        list.splice(tempIndex, 1)
      } else if (tempIndex !== -1) {
        list.splice(tempIndex, 1, { ...list[tempIndex], ...message, pending: false, failed: false })
      } else if (realIndex === -1 && message) {
        list.push(message)
      }
      list.sort(compareMessages)
      chat.messages = list
      vue.$root.$emit('system::chat::messages::updated', sessionId)
    },
    markMessageFailed(state, { tempId, sessionId }) {
      const chat = state.chatMap[sessionId]
      if (!chat) return
      const index = chat.messages.findIndex(msg => msg.messageId === tempId)
      if (index !== -1) {
        chat.messages.splice(index, 1, { ...chat.messages[index], pending: true, failed: true })
      }
    },
    removeMessage(state, { messageId, sessionId }) {
      const chat = state.chatMap[sessionId]
      if (!chat) return
      chat.messages = chat.messages.filter(msg => msg.messageId !== messageId)
    },
    markClientSeen(state, sessionId) {
      const chat = state.chatMap[sessionId]
      if (!chat) return
      chat.messages = chat.messages.map(msg => (msg.isClientSide || msg.pending || msg.clientHasSeen) ? msg : { ...msg, clientHasSeen: true })
    },
    updateUnreadMessages(state, unreadBySessionMap) {
      delete unreadBySessionMap["total"];
      Object.values(state.sessions).forEach(session => {
        const unseenMsgs = unreadBySessionMap[session.id] || 0
        if (session.unseenMsgs !== unseenMsgs) {
          Vue.set(state.sessions, session.id, { ...session, unseenMsgs })
        }
      })
    },
    setDraft(state, { sessionId, text }) {
      if (sessionId == null) return
      if (text) {
        Vue.set(state.drafts, sessionId, text)
      } else {
        Vue.delete(state.drafts, sessionId)
      }
    },
    setTyping(state, sessionId) {
      Vue.set(state.typing, sessionId, Date.now())
    },
    clearTyping(state, sessionId) {
      Vue.delete(state.typing, sessionId)
    },
    clearActiveChat(state) {
      state.activeChat = null
    }, // Clear the currently active chat
    clearChats(state) {
      state.chatMap = {}
    }, // Clear all chats
  },
  actions: {
    fetchChatSessions({commit}) {
      return new Promise((resolve, reject) => {
        api.base()
          .get('/chat/sessions')
          .then(response => {
            let metadata = response.data.metadata || {}
            let sessions = response.data.data || []
            sessions.forEach(session => {
              session.unseenMsgs = metadata.unread ? (metadata.unread[session.id] || 0) : 0
            })
            commit('setSessions', sessions || [])
            resolve(sessions || [])
          })
          .catch(error => reject(error))
      })
    },
    fetchChatSession({commit}, sessionId) {
      return new Promise((resolve, reject) => {
        api.base()
          .get('/chat/sessions/'+sessionId)
          .then(response => {
            commit('setSession', response.data.data)
            resolve(response.data.data)
          })
          .catch(error => reject(error))
      })
    },
    initChatSession({ commit }, clientId) {
      return new Promise((resolve, reject) => {
        api.base()
          .post('/chat/session/init/'+clientId)
          .then(response => {
            commit('setSession', response.data.data)
            commit('setActiveChat', response.data.data.id)
            resolve(response.data.data)
          })
          .catch(error => reject(error))
      })
    },
    getChatMessages({commit}, { sessionId, page = 0, limit = 1000 }) {
        return new Promise((resolve, reject) => {
            api.base()
            .get(`/chat/messages/active/${sessionId}`, { params: { page, size: limit } })
            .then(response => {
                const messages = response.data.data || []
                commit('updateChat', messages)
                if (messages.length > 0) {
                  // Newest first from the server
                  commit('setSessionLastMessage', messages.slice().sort(compareMessages)[messages.length - 1])
                }
                resolve(messages)
            })
            .catch(error => reject(error))
        })
    },
    receiveMessage({ commit }, message) {
      if (message == null || message.chatSessionId == null) return
      commit('updateChat', [message])
      commit('setSessionLastMessage', message)
      commit('clearTyping', message.chatSessionId)
    },
    sendChatMessage({ commit, rootGetters }, { sessionId, messageRequest, tempId }) {
      // Reuse the id on retry so the failed bubble is replaced instead of duplicated
      tempId = tempId || ('tmp-' + Date.now() + '-' + Math.floor(Math.random() * 100000))
      const tempMessage = {
        ...messageRequest,
        messageId: tempId,
        localKey: tempId,
        chatSessionId: sessionId,
        sessionId,
        senderId: rootGetters['user/myId'],
        isClientSide: false,
        clientHasSeen: false,
        pending: true,
        failed: false,
        timestamp: Vue.moment.utc().format('YYYY-MM-DD HH:mm'),
      }
      commit('addMessage', tempMessage)
      return api.base()
        .post(`/chat/messages/${sessionId}/send`, messageRequest)
        .then(response => {
          const message = response.data.data
          commit('updateMessage', { tempId, message: { ...message, pending: false, failed: false }, sessionId })
          commit('setSessionLastMessage', message)
          return response.data
        })
        .catch(error => {
          commit('markMessageFailed', { tempId, sessionId })
          throw error
        })
    },
    fetchUnreadMessagesCount({ commit, getters }) {
        return new Promise((resolve, reject) => {
            api.base()
            .get('/chat/messages/unread/count')
            .then(response => {
                commit("updateUnreadMessages", {...(response.data.data||{})});
                resolve(response.data.data)
            })
            .catch(error => reject(error))
        })
    },
    setMessagesRead({ commit, getters }, { sessionId, messageIds }) {
        return new Promise((resolve, reject) => {
            api.base()
            .post(`/chat/session/${sessionId}/read`, { messageIds })
            .then(response => {
                const session = response.data.data;
                commit('setSession', { ...session, unseenMsgs: 0 })
                vue.$root.$emit('system::chat::unread::changed')
                resolve(session)
            })
            .catch(error => reject(error))
        })
    },
    finalizeSession({ commit, getters }, clientId) {
      return new Promise((resolve, reject) => {
        api.base()
          .post('/chat/session/finalize/' + clientId)
          .then(response => {
            commit('removeSession', response.data.data.id)
            resolve(response.data.data)
          })
          .catch(error => reject(error))
      })
    },
    notifyTypingMessage({ commit, getters }, sessionId) {
      return api.base().post(`/chat/messages/${sessionId}/typing`, {})
    },
  },
}
